import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Table, Space, Tag, Typography, Image, Modal, Button } from 'antd';
import { InboxOutlined, WarningFilled, UserOutlined, PhoneOutlined, EnvironmentOutlined, CheckCircleFilled, ExclamationCircleFilled, PictureOutlined, WarningOutlined, CheckCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { MergedOrder, OrderStatusMap, LogisticsStateMap } from '../../../types/process/order';
import { pickDurationRange } from '../../../types/process/order-audit';
import { useShopMap, useStaffMap } from '../../../services/store/organization.store';
import OrderProgressBar from './OrderProgressBar';
import { formatOrderRiskType, generateOrderUrl } from './effect';
import { DeviceTagColorMap, RiskTagColorMap, parseSkuNameParts } from './BusinessStyles';
import { ORDER_STATUS_LABEL_MAP, LogisticsNameMap } from '../../../types/process/order-data';

const { Text, Link } = Typography;

export type ShowField = 'auditStep' | 'base' | 'preAudit' | 'delivery' | 'receipt' | 'pendingDelivery' | 'receiptManagement' | 'groupChatPending';

interface SceneConfig {
  showProgressBar: boolean;
  columns: string[];
}

interface OrderSceneTableProps {
  dataSource: MergedOrder[];
  renderActions?: (order: MergedOrder) => React.ReactNode;
  showField?: ShowField;
  activeScene?: string;
  rowSelection?: any;
}

type TableRow = MergedOrder & { _rowType?: 'main' | 'progress'; _originalIndex?: number };

const formatDateTime = (v?: string | number) => {
  if (v === undefined || v === null || v === '') return '-';
  const d = typeof v === 'number' ? dayjs(v) : dayjs(v);
  if (!d.isValid()) return String(v);
  return d.format('YYYY-MM-DD HH:mm:ss');
};

const formatDurationSeconds = (seconds?: number) => {
  if (seconds === undefined || seconds === null) return '-';
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const parts: string[] = [];
  if (h > 0) parts.push(`${h}小时`);
  if (m > 0) parts.push(`${m}分`);
  if (s > 0 || parts.length === 0) parts.push(`${s}秒`);
  return parts.join('');
};

const SCENE_CONFIGS: Record<ShowField, SceneConfig> = {
  base: {
    showProgressBar: false,
    columns: ['index', 'store', 'consignee', 'isPaid', 'orderCreatedAt', 'suspendReason', 'orderNo', 'product', 'deviceType', 'tenancyMonths', 'riskType', 'brandModel']
  },
  auditStep: {
    showProgressBar: true,
    columns: ['index', 'store', 'consignee', 'isPaid', 'orderCreatedAt', 'suspendReason', 'orderNo', 'product', 'deviceType', 'tenancyMonths', 'riskType', 'brandModel', 'issueDeadline', 'operatorName', 'auditName', 'auditBeginTime', 'auditEndTime', 'auditDuration']
  },
  preAudit: {
    showProgressBar: false,
    columns: ['index', 'store', 'consignee', 'product', 'deviceType', 'tenancyMonths', 'isPaid', 'riskType', 'issueDeadline', 'orderCreatedAt', 'suspendReason', 'orderNo']
  },
  delivery: {
    showProgressBar: false,
    columns: ['index', 'store', 'orderNo', 'consignee', 'product', 'deliveryType', 'logisticsNo', 'logisticsStatus', 'deliveryTime', 'orderCreatedAt']
  },
  receipt: {
    showProgressBar: false,
    columns: ['index', 'store', 'orderNo', 'consignee', 'product', 'logisticsNo', 'logisticsStatus', 'deliveryTime', 'rrzuStatus', 'orderCreatedAt']
  },
  pendingDelivery: {
    showProgressBar: false,
    columns: ['store', 'orderInfo_delivery', 'customerProduct_delivery', 'actualPayment_delivery', 'status_delivery', 'decisionTime_delivery', 'logistics_delivery', 'warning_delivery']
  },
  receiptManagement: {
    showProgressBar: false,
    columns: ['index', 'orderNo', 'store', 'productTitle_receipt', 'userAddress_receipt', 'logistics_receipt', 'rrzuEvidence_receipt']
  },
  groupChatPending: {
    showProgressBar: false,
    columns: ['index', 'store', 'orderInfo_delivery', 'customerProduct_delivery', 'operatorName', 'decisionName', 'decisionTime_groupChat', 'groupChatStatus']
  }
};

const OrderSceneTable: React.FC<OrderSceneTableProps> = ({
  dataSource,
  renderActions,
  showField = 'base',
  activeScene,
  rowSelection,
}) => {
  const { shopMap } = useShopMap();
  const { staffMap } = useStaffMap();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableWidth, setTableWidth] = useState<number>(0);

  useEffect(() => {
    if (!tableContainerRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setTableWidth(entry.contentRect.width);
      }
    });
    resizeObserver.observe(tableContainerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const currentConfig = SCENE_CONFIGS[showField] || SCENE_CONFIGS.base;
  const { showProgressBar: defaultShowProgressBar } = currentConfig;
  
  const [isProgressBarExpanded, setIsProgressBarExpanded] = useState<boolean>(false);

  const columns: ColumnsType<TableRow> = useMemo(() => {
    const columnMap: Record<string, any> = {
      index: {
        title: '序号',
        key: 'index',
        width: 60,
        fixed: 'left',
        render: (_: any, record: TableRow, index: number) => {
          return index + 1;
        },
      },
      store: {
        title: '店铺',
        key: 'store',
        width: 100,
        fixed: 'left',
        render: (_: any, record: TableRow) => {
          const shop = shopMap.get(record.order.storeId);
          return <Text strong>{shop?.name || String(record.order.storeId ?? '-')}</Text>;
        },
      },
      orderInfo_delivery: {
        title: '订单信息',
        key: 'orderInfo_delivery',
        width: 160,
        render: (_: any, record: TableRow) => {
          const v = record.order.orderNo;
          const { label } = formatOrderRiskType({ blindType: record.order.blindType, riskType: record.order.riskType });
          const createdAt = formatDateTime(record.order.orderCreatedAt);
          return (
            <Space direction="vertical" size={2}>
              <Link
                href={generateOrderUrl({ orderNo: v, targetScene: 'tab_all', targetPath: '/order/list' })}
                target="_blank"
                style={{ color: '#101828', fontWeight: 500 }}
              >
                {v}
              </Link>
              <Text type="secondary" style={{ fontSize: 12 }}>{createdAt !== '-' ? dayjs(record.order.orderCreatedAt).format('YYYY/MM/DD HH:mm') : '-'}</Text>
              <Tag color={RiskTagColorMap[label] || 'default'} style={{ borderRadius: 2, border: '1px solid #91caff', color: '#1677ff', background: '#e6f4ff', width: 'fit-content' }}>
                {label}
              </Tag>
            </Space>
          );
        },
      },
      customerProduct_delivery: {
        title: '客户/商品',
        key: 'customerProduct_delivery',
        width: 180,
        render: (_: any, record: TableRow) => {
          const name = record.orderData?.userAuthName || record.order?.userName || '-';
          const product = record.order.productTitle || record.orderData?.productTitle || '-';
          return (
            <Space direction="vertical" size={2}>
              <Text style={{ fontSize: 13, color: '#101828' }}>{name}</Text>
              <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'normal' }}>{product}</Text>
            </Space>
          );
        },
      },
      actualPayment_delivery: {
        title: '实付款',
        key: 'actualPayment_delivery',
        width: 150,
        render: (_: any, record: TableRow) => {
          const amount = record.orderData?.hadPayRental ?? record.orderData?.firstRent ?? 0;
          const payTime = record.orderData?.paySuccessDate ? formatDateTime(record.orderData.paySuccessDate) : '-';
          return (
            <Space direction="vertical" size={2}>
              <Text style={{ fontSize: 13, color: '#6a7282' }}>实付款：<span style={{ color: '#101828' }}>¥{amount}</span></Text>
              <Text style={{ fontSize: 12, color: '#ef4444' }}>付款时间：{payTime}</Text>
            </Space>
          );
        },
      },
      status_delivery: {
        title: '订单/工单状态',
        key: 'status_delivery',
        width: 170,
        render: (_: any, record: TableRow) => {
          const rrzuStatus = record.order.rrzuStatus ? (ORDER_STATUS_LABEL_MAP[record.order.rrzuStatus] || String(record.order.rrzuStatus)) : '-';
          const sysStatus = OrderStatusMap[record.order.orderStatus] || String(record.order.orderStatus);
          return (
            <Space direction="vertical" size={4}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#facc15' }} />
                <Text style={{ fontSize: 12, color: '#6a7282' }}>人人租订单状态：<span style={{ color: '#101828' }}>{rrzuStatus}</span></Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#facc15' }} />
                <Text style={{ fontSize: 12, color: '#6a7282' }}>工单处理状态：<span style={{ color: '#101828' }}>{sysStatus}</span></Text>
              </div>
            </Space>
          );
        },
      },
      decisionTime_delivery: {
        title: '决策时间',
        key: 'decisionTime_delivery',
        width: 160,
        render: (_: any, record: TableRow) => {
          const time = record.order.decisionTime;
          if (!time) return '-';
          const hours = dayjs().diff(dayjs(time), 'hour');
          return (
            <Space direction="vertical" size={2}>
              <Text style={{ fontSize: 13, color: '#101828' }}>{formatDateTime(time)}</Text>
              <Text style={{ fontSize: 12, color: '#ef4444' }}>距离决策完成时间已过去{hours}小时</Text>
            </Space>
          );
        },
      },
      decisionTime_groupChat: {
        title: '放单时间',
        key: 'decisionTime_groupChat',
        width: 160,
        render: (_: any, record: TableRow) => {
          const time = record.order.decisionTime;
          if (!time) return '-';
          return <Text style={{ fontSize: 13, color: '#101828' }}>{formatDateTime(time)}</Text>;
        },
      },
      groupChatStatus: {
        title: '状态',
        key: 'groupChatStatus',
        width: 120,
        render: (_: any, record: TableRow) => {
          const groupStatus = (record.order as any).orderMarker?.groupStatus || 0;
          if (groupStatus === 1) {
            return <Tag color="success" style={{ borderRadius: 14 }}>已建群</Tag>;
          }
          return <Tag color="default" style={{ borderRadius: 14 }}>未建群</Tag>;
        },
      },
      logistics_delivery: {
        title: '物流信息',
        key: 'logistics_delivery',
        width: 160,
        render: (_: any, record: TableRow) => {
          const noLogistics = !record.order.logisticsNo;
          if (noLogistics) {
            return (
              <Space size={4} style={{ color: '#2b7fff' }}>
                <InboxOutlined />
                <Text style={{ color: '#2b7fff', fontSize: 13 }}>无物流信息</Text>
              </Space>
            );
          }
          const logisticNameRaw = record.orderData?.logisticsInfo?.logisticName || 'SF';
          const carrier = LogisticsNameMap[logisticNameRaw] || logisticNameRaw || '顺丰速运';
          return (
            <Space direction="vertical" size={2}>
              <Space size={4} style={{ color: '#2b7fff' }}>
                <InboxOutlined />
                <Text style={{ color: '#2b7fff', fontSize: 13 }}>
                  {record.order.logisticsStatus ? (LogisticsStateMap[record.order.logisticsStatus] || record.order.logisticsStatus) : '待揽收'}
                </Text>
              </Space>
              <Text type="secondary" style={{ fontSize: 12 }}>{carrier} {record.order.logisticsNo}</Text>
            </Space>
          );
        },
      },
      warning_delivery: {
        title: '预警/提示',
        key: 'warning_delivery',
        width: 180,
        render: (_: any, record: TableRow) => {
          const deliveryStatus = record.order.deliveryStatus || 0;
          // 业务流优化前默认为“平台发货”
          const isSelfDelivery = deliveryStatus >= 11 && deliveryStatus <= 13;
          const isPlatformDelivery = !isSelfDelivery;
          const noLogistics = !record.order.logisticsNo;
          
          let warningTag = noLogistics ? '待生成物流单号' : (record.order.logisticsStatus ? (LogisticsStateMap[record.order.logisticsStatus] || record.order.logisticsStatus) : '待揽收');
          let promptText = '';
          
          if (isPlatformDelivery) {
            promptText = '请前往平台催促发货！';
          } else if (isSelfDelivery) {
            promptText = '请及时联系采购人员催促发货！';
          }

          const tagStyle = isPlatformDelivery
            ? { color: '#1677ff', backgroundColor: '#e6f4ff', borderColor: '#91caff' } // 蓝色背景
            : { color: '#d97706', backgroundColor: '#fef3c7', borderColor: '#fde68a' }; // 黄色背景

          return (
            <Space direction="vertical" size={4} align="start">
              <Tag style={{ ...tagStyle, borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4, padding: '2px 8px' }}>
                <WarningFilled /> {warningTag}
              </Tag>
              <Text style={{ fontSize: 12, color: '#ef4444' }}>{promptText}</Text>
            </Space>
          );
        },
      },
      productTitle_receipt: {
        title: '商品标题',
        key: 'productTitle_receipt',
        width: 180,
        render: (_: any, record: TableRow) => {
          return <Text style={{ whiteSpace: 'normal' }}>{record.order.productTitle || record.orderData?.productTitle || '-'}</Text>;
        }
      },
      userAddress_receipt: {
        title: '用户地址',
        key: 'userAddress_receipt',
        width: 220,
        render: (_: any, record: TableRow) => {
          const name = record.orderData?.userAuthName || record.order?.userName || '-';
          const phone = record.orderData?.userPhone || record.order?.userPhone || '-';
          const address = record.orderData?.userAddress || '-';
          return (
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              <Space size={4}>
                <UserOutlined style={{ color: '#8c8c8c' }} />
                <Text style={{ fontSize: 13 }}>{name}</Text>
              </Space>
              <Space size={4}>
                <PhoneOutlined style={{ color: '#8c8c8c' }} />
                <Text style={{ fontSize: 13 }}>{phone}</Text>
                {phone !== '-' && <Typography.Paragraph copyable={{ text: phone }} style={{ margin: 0 }} />}
              </Space>
              <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                <EnvironmentOutlined style={{ color: '#8c8c8c', marginTop: 4 }} />
                <Text type="secondary" style={{ fontSize: 12, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'normal' }} title={address}>
                  {address}
                </Text>
              </div>
            </Space>
          );
        }
      },
      logistics_receipt: {
        title: '物流状态',
        key: 'logistics_receipt',
        width: 160,
        render: (_: any, record: TableRow) => {
          const noLogistics = !record.order.logisticsNo;
          if (noLogistics) {
            return (
              <Space size={4} style={{ color: '#2b7fff' }}>
                <InboxOutlined />
                <Text style={{ color: '#2b7fff', fontSize: 13 }}>无物流信息</Text>
              </Space>
            );
          }
          const logisticNameRaw = record.orderData?.logisticsInfo?.logisticName || 'SF';
          const carrier = LogisticsNameMap[logisticNameRaw] || logisticNameRaw || '顺丰速运';
          return (
            <Space direction="vertical" size={2}>
              <Space size={4} style={{ color: '#2b7fff' }}>
                <InboxOutlined />
                <Text style={{ color: '#2b7fff', fontSize: 13 }}>
                  {record.order.logisticsStatus ? (LogisticsStateMap[record.order.logisticsStatus] || record.order.logisticsStatus) : '待揽收'}
                </Text>
              </Space>
              <Text type="secondary" style={{ fontSize: 12 }}>{carrier} {record.order.logisticsNo}</Text>
            </Space>
          );
        }
      },
      rrzuEvidence_receipt: {
        title: '人人租平台上传状态',
        key: 'rrzuEvidence_receipt',
        width: 180,
        render: (_: any, record: TableRow) => {
          const evidenceList = record.orderRecord?.evidence || [];
          // phase 签收凭证 可能是 9 或者 '9'，或者 type 是 '9'
          const evidence = evidenceList.find((e: any) => String(e.phase) === '9' || String(e.type) === '9');
          const isUploaded = !!evidence || !!record.order.receiptEvidence;

          if (isUploaded) {
            const uploadTime = evidence?.times_tamp || '-';

            return (
              <Space direction="vertical" size={2} align="start" style={{ width: '100%' }}>
                <Tag style={{ color: '#52c41a', backgroundColor: '#f6ffed', borderColor: '#b7eb8f', borderRadius: 14, display: 'flex', alignItems: 'center', gap: 4, padding: '2px 10px', margin: 0 }}>
                  <CheckCircleOutlined /> 已上传
                </Tag>
                {uploadTime !== '-' && <Text type="secondary" style={{ fontSize: 12 }}>{uploadTime}</Text>}
              </Space>
            );
          }
          
          return (
            <Tag style={{ color: '#ff4d4f', backgroundColor: '#fff2f0', borderColor: '#ffccc7', borderRadius: 14, display: 'flex', alignItems: 'center', gap: 4, padding: '2px 10px', margin: 0, width: 'fit-content' }}>
              <WarningOutlined /> 未上传
            </Tag>
          );
        }
      },
      consignee: {
        title: '收货信息',
        key: 'consignee',
        width: 150,
        fixed: 'left',
        render: (_: any, record: TableRow) => {
          const name = record.orderData?.userAuthName || record.order?.userName || '-';
          const phone = record.orderData?.userPhone || record.order?.userPhone || '-';

          return (
            <Space direction="vertical" size={2}>
              <Space size={4}>
                <Text style={{ fontSize: 13 }}>{name}</Text>
                {name !== '-' && <Typography.Paragraph copyable={{ text: name }} style={{ margin: 0 }} />}
              </Space>
              <Text type="secondary" style={{ fontSize: 12 }}>{phone}</Text>
            </Space>
          );
        },
      },
      isPaid: {
        title: '首期租金',
        key: 'isPaid',
        width: 100,
        fixed: 'left',
        render: (_: any, record: TableRow) => {
          const paid = !!record.orderData?.paySuccessDate || (record.orderData?.hadPayRental ?? 0) > 0;
          return paid
            ? <span style={{ color: '#52c41a' }}>是</span>
            : <span style={{ color: '#ff4d4f' }}>否</span>;
        },
      },
      orderCreatedAt: {
        title: '进件时间',
        key: 'orderCreatedAt',
        width: 160,
        fixed: 'left',
        render: (_: any, record: TableRow) => {
          const status = record.order.orderStatus;

          let dateContent = <span>{formatDateTime(record.order.orderCreatedAt) || '-'}</span>;

          if (status === 30) {
            return (
              <Space direction="vertical" size={4}>
                {dateContent}
                <Tag color="default">已取消</Tag>
              </Space>
            );
          }

          return dateContent;
        },
      },
      suspendReason: {
        title: '暂存原因',
        key: 'suspendReason',
        width: 100,
        fixed: 'left',
        render: (_: any, record: TableRow) => {
          // 2 表示挂起 (暂存)
          if (record.audit?.status !== 2) {
            return '-';
          }
          const reason = pickDurationRange(record.audit?.durationRange, 'suspendReason', 'last');
          return reason ? <Text style={{ color: '#6a7282' }}>{reason}</Text> : '-';
        },
      },
      orderNo: {
        title: '订单ID',
        dataIndex: ['order', 'orderNo'],
        key: 'orderNo',
        width: 170,
        fixed: 'left',
        render: (v: string) => {
          return (
            <Space size={4}>
              <Link
                href={generateOrderUrl({ orderNo: v, targetScene: activeScene })}
                target="_blank"
              >
                {v}
              </Link>
              <Typography.Paragraph copyable={{ text: v }} style={{ margin: 0 }} />
            </Space>
          );
        },
      },
      product: {
        title: '品牌/型号/内存',
        key: 'product',
        width: 180,
        render: (_: any, record: TableRow) => {
          const brand = record.order.productBrand || record.orderData?.brandName || '';
          const model = record.order.productSku || record.orderData?.model || '';
          const parsedParams = parseSkuNameParts(record.orderData?.skuName);
          const memory = parsedParams.memory || '';

          return (
            <Space direction="vertical" size={2}>
              <Text>{brand}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {[model, memory].filter(Boolean).join(' ')}
              </Text>
            </Space>
          );
        },
      },
      deviceType: {
        title: '设备机型',
        key: 'deviceType',
        width: 90,
        render: (_: any, record: TableRow) => {
          const brandName = record.orderData?.brandName || record.order.productBrand || '';
          if (!brandName) return '-';

          const isOld = record.orderData?.isOldMobilePhone;
          const statusMatch = isOld ? '二手' : '全新';
          const osMatch = brandName.includes('苹果') || brandName.toUpperCase().includes('APPLE') ? '苹果' : '安卓';
          const dt = `${statusMatch}${osMatch}`;

          const styleConfig = DeviceTagColorMap[dt];
          if (styleConfig) {
            return (
              <Tag
                style={{
                  color: styleConfig.color,
                  background: styleConfig.background,
                  borderColor: 'transparent',
                  borderRadius: 2,
                }}
              >
                {dt}
              </Tag>
            );
          }
          return <Tag color="default" style={{ borderRadius: 2 }}>{dt}</Tag>;
        },
      },
      tenancyMonths: {
        title: '租赁期数',
        key: 'tenancyMonths',
        width: 90,
        render: (_: any, record: TableRow) => {
          const count = record.orderData?.receivableInstallmentPeriodsCount;
          return count ? `${count}期` : '-';
        },
      },
      riskType: {
        title: '风控类型',
        key: 'riskType',
        width: 90,
        render: (_: any, record: TableRow) => {
          const { label } = formatOrderRiskType({ blindType: record.order.blindType, riskType: record.order.riskType });
          return <Tag color={RiskTagColorMap[label] || 'default'} style={{ borderRadius: 2, border: 'none' }}>{label}</Tag>;
        },
      },
      brandModel: {
        title: '商品信息',
        key: 'brandModel',
        width: 180,
        render: (_: any, record: TableRow) => {
          return (
            <Space direction="vertical" size={0}>
              <Text>{record.order.productTitle || record.orderData?.productTitle || '-'}</Text>
            </Space>
          );
        },
      },
      issueDeadline: {
        title: '决策截止时间',
        key: 'issueDeadline',
        width: 140,
        render: (_: any, record: TableRow) => {
          const deadline = record.orderRecord?.lockStatus?.issue_deadline_text;
          return deadline || '-';
        },
      },
      operatorName: {
        title: '运营',
        key: 'operatorName',
        width: 110,
        render: (_: any, record: TableRow) => {
          const staff = staffMap.get(record.order.preprocessOperatorId || 0);
          return staff?.name || (record.order.preprocessOperatorId ? String(record.order.preprocessOperatorId) : '-');
        },
      },
      decisionName: {
        title: '决策人',
        key: 'decisionName',
        width: 110,
        render: (_: any, record: TableRow) => {
          const staff = staffMap.get(record.order.decisionOperatorId || 0);
          return staff?.name || (record.order.decisionOperatorId ? String(record.order.decisionOperatorId) : '-');
        },
      },
      rrzuStatus: {
        title: '人人租状态',
        key: 'rrzuStatus',
        width: 110,
        render: (_: any, record: TableRow) => {
          const status = record.order.rrzuStatus;
          return status ? (ORDER_STATUS_LABEL_MAP[status] || String(status)) : '-';
        },
      },
      decisionTime: {
        title: '决策时间',
        key: 'decisionTime',
        width: 160,
        render: (_: any, record: TableRow) => {
          return formatDateTime(record.order.decisionTime);
        },
      },
      auditName: {
        title: '审核姓名',
        key: 'auditName',
        width: 110,
        render: (_: any, record: TableRow) => {
          const staff = staffMap.get(record.order.auditId);
          return staff?.name || (record.order.auditId ? String(record.order.auditId) : '-');
        },
      },
      auditBeginTime: {
        title: '审核开始时间',
        key: 'auditBeginTime',
        width: 160,
        render: (_: any, record: TableRow) => {
          const auditId = record.order.auditId;
          if (!auditId) return '-';
          const startMs = pickDurationRange(record.audit?.durationRange, 'startTime', { operatorId: auditId });
          return typeof startMs === 'number' ? formatDateTime(startMs) : '-';
        },
      },
      auditEndTime: {
        title: '审核结束时间',
        key: 'auditEndTime',
        width: 160,
        render: (_: any, record: TableRow) => {
          const auditId = record.order.auditId;
          if (!auditId) return '-';
          const endMs = pickDurationRange(record.audit?.durationRange, 'endTime', { operatorId: auditId });
          return typeof endMs === 'number' ? formatDateTime(endMs) : '-';
        },
      },
      auditDuration: {
        title: '审核时长',
        key: 'auditDuration',
        width: 100,
        render: (_: any, record: TableRow) => {
          return formatDurationSeconds(record.audit?.durationTime);
        },
      },
      deliveryType: {
        title: '决策结果',
        key: 'deliveryType',
        width: 100,
        render: (_: any, record: TableRow) => {
          const status = record.order.deliveryStatus || 0;
          if (status >= 1 && status <= 3) return '平台发货';
          if (status >= 11 && status <= 13) return '自采发货';
          return '-';
        },
      },
      logisticsNo: {
        title: '物流单号',
        key: 'logisticsNo',
        width: 150,
        render: (_: any, record: TableRow) => {
          return record.order.logisticsNo || '-';
        },
      },
      logisticsStatus: {
        title: '物流状态',
        key: 'logisticsStatus',
        width: 100,
        render: (_: any, record: TableRow) => {
          return record.order.logisticsStatus ? (LogisticsStateMap[record.order.logisticsStatus] || record.order.logisticsStatus) : '-';
        }
      },
      deliveryTime: {
        title: '发货时间',
        key: 'deliveryTime',
        width: 160,
        render: (_: any, record: TableRow) => {
          // Assuming shipAt is in orderData or constructed
          return record.orderData?.shipAt ? formatDateTime(record.orderData.shipAt) : '-';
        },
      },
      receiptTime: {
        title: '签收时间',
        key: 'receiptTime',
        width: 160,
        render: (_: any, record: TableRow) => {
          // Assuming takeAt is in orderData
          return record.orderData?.takeAt ? formatDateTime(record.orderData.takeAt) : '-';
        },
      },
      action: {
        title: '操作',
        key: 'action',
        fixed: 'right',
        width: 140,
        render: (_: any, record: TableRow) => {
          return renderActions ? renderActions(record) : null;
        },
      }
    };

    const finalColumns: ColumnsType<TableRow> = currentConfig.columns.map(key => columnMap[key]).filter(Boolean);

    if (renderActions) {
      finalColumns.push(columnMap.action);
    }

    return finalColumns;
  }, [shopMap, staffMap, renderActions, showField]);

  return (
    <div ref={tableContainerRef} style={{ width: '100%' }}>
      {defaultShowProgressBar && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <Button size="small" onClick={() => setIsProgressBarExpanded(!isProgressBarExpanded)}>
            {isProgressBarExpanded ? '收起进度条' : '展开进度条'}
          </Button>
        </div>
      )}
      {showField === 'pendingDelivery' && (
        <style>{`
          .delivery-platform-row { background-color: #f0f7ff !important; }
          .delivery-platform-row:hover > td { background-color: #e6f4ff !important; }
          .delivery-self-row { background-color: #fffbe6 !important; }
          .delivery-self-row:hover > td { background-color: #fff1b8 !important; }
        `}</style>
      )}
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey={(r) => r.order.orderNo}
        scroll={{ x: 1400 }} // Narrower scroll
        pagination={false}
        rowSelection={rowSelection}
        expandable={
          defaultShowProgressBar
            ? {
                expandedRowRender: (record) => (
                  <div style={{ padding: '8px 24px', width: '100%', overflowX: 'auto' }}>
                    <OrderProgressBar order={record} />
                  </div>
                ),
                expandedRowKeys: isProgressBarExpanded ? dataSource.map((r) => r.order.orderNo) : [],
                expandIconColumnIndex: -1, // Hide the default expand icon
              }
            : undefined
        }
        rowClassName={(r) => {
          const record = r as TableRow;
          if (showField === 'pendingDelivery') {
            const deliveryStatus = record.order.deliveryStatus || 0;
            if (deliveryStatus >= 1 && deliveryStatus <= 3) return 'delivery-platform-row';
            if (deliveryStatus >= 11 && deliveryStatus <= 13) return 'delivery-self-row';
          }
          return '';
        }}
        size="small" // Use small size to compact the table
      />
    </div>
  );
};

export default OrderSceneTable;
